import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguageContext } from '../../../../../contexts/LanguageContext';
import Dialog from '../../../../../components/Dialog';
import { ActionButton } from '../../../../../components/Button';
import { Box, Grid } from '@mui/material';
import DefaultConstants from '../../../../../data/Constants';
import { IDefaultDialogFilterProps } from '../../../../../interfaces/IReport';
import DateTimeHelper from '../../../../../helpers/DateTimeHelper';
import DateField from '../../../../../components/DateField';
import SelectField from '../../../../../components/SelectField';
import { useUserContext } from '../../../../../contexts/UserContext';
import IEnterprise from '../../../../../interfaces/IEnterprise';
import IAccessProfile from '../../../../../interfaces/IAccessProfile';
import Switch from '../../../../../components/Switch';


export interface IStatisticalLogReportFilters {
    enterprise: IEnterprise | undefined;
    accessProfile: IAccessProfile[];
    startDate: string;
    endDate: string;
    hideZeroedUsers: boolean;
}


const initialFilters: IStatisticalLogReportFilters = {
    enterprise: undefined,
    accessProfile: [],
    startDate: DateTimeHelper.Now('YYYY-MM-DD'),
    endDate: DateTimeHelper.Now('YYYY-MM-DD'),
    hideZeroedUsers: false
};



interface IStatisticalLogReportDialogFilterProps extends IDefaultDialogFilterProps {
    accessProfileList: IAccessProfile[];
}

const StatisticalLogFilters: React.FunctionComponent<IStatisticalLogReportDialogFilterProps> = ({
    open, 
    handleClose,
    handleOpenPreview,
    enterpriseList,
    accessProfileList
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useUserContext();
    
    const [filters, setFilters] = useState<IStatisticalLogReportFilters>({
        ...initialFilters,
        enterprise: enterprise
    });
    const [startDate, setStartDate] = useState<string | undefined>(initialFilters.startDate);
    const [endDate, setEndDate] = useState<string | undefined>(initialFilters.endDate);


    useEffect(() => {
        if (open) {
            setFilters({
                ...initialFilters, 
                enterprise: enterprise
            });
            setStartDate(initialFilters.startDate);
            setEndDate(initialFilters.endDate);
        }

        // eslint-disable-next-line
    }, [open]);


    useEffect(() => {
        setFilters({
            ...filters,
            accessProfile: []
        });

        // eslint-disable-next-line
    }, [filters.enterprise]);


    const handleChangeFilterValue = useCallback((name: string, value: any) => {
        setFilters({
            ...filters,
            [name]: value
        });
        // eslint-disable-next-line
    }, [filters]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='close' onClick={handleClose} />,
            <ActionButton action='view' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const _filters = {
            ...filters,
            startDate: startDate,
            endDate: endDate
        };

        if (startDate && endDate && startDate > endDate) {
            const aux = startDate;
            _filters.startDate = endDate;
            _filters.endDate = aux;
        }

        handleOpenPreview(_filters);

        setStartDate(_filters.startDate);
        setEndDate(_filters.endDate);
        // eslint-disable-next-line
    }, [filters, startDate, endDate]);


    return (
        <Dialog 
            open={open}
            handleClose={handleClose}
            title={translate('statisticalLog')}
            fullWidth={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
            maxWidth='md'
            fullHeight={true}
        >
            <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <SelectField
                        label={translate('enterprise')}
                        name='enterprise'
                        value={filters.enterprise?.id ?? 0}
                        onChange={e => {
                            const { name, value } = e.target;
                            const enterprise = enterpriseList.find(e => +e.id === +value);
                            handleChangeFilterValue(name, enterprise);
                        }}
                        required={true}
                        data={enterpriseList.map(e => {
                            return {
                                value: +e.id,
                                text: e.name
                            };
                        })}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={DateTimeHelper.ToDate(startDate)}
                        onChange={value => setStartDate(DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item lg={3} md={3} sm={6} xs={6}>
                    <DateField
                        label={translate('endDate')}
                        format='DD/MM/YYYY'
                        name='endDate'
                        value={DateTimeHelper.ToDate(endDate)}
                        onChange={value => setEndDate(DateTimeHelper.Format(value, 'YYYY-MM-DD'))}
                        required={true}
                        disableFuture={true}
                    />
                </Grid>

                <Grid item xs={12} />

                <Grid item lg={4} md={4} sm={6} xs={12}>
                    <SelectField
                        label={translate('accessProfile')}
                        name='accessProfile'
                        value={filters.accessProfile.length > 0 ? filters.accessProfile.map(a => +a.id) : [0]}
                        required={false}
                        multiple={true}
                        onChange={e => {
                            const { name, value } = e.target;
                            const values = value as number[];

                            const accessProfile = filters.accessProfile.length > 0 && values.includes(0) ? [] : accessProfileList.filter(a => values.includes(+a.id));
                            handleChangeFilterValue(name, accessProfile);
                        }}
                        data={[{ value: 0, text: translate('all') }].concat(accessProfileList.map(a => {
                            return {
                                value: +a.id,
                                text: a.description
                            };
                        }))}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box padding={2}>
                        <Switch
                            label={translate('_hideZeroedUsers')}
                            labelPlacement='end'
                            name='hideZeroedUsers'
                            value={filters.hideZeroedUsers ? 0 : 1}
                            checked={filters.hideZeroedUsers}
                            onChange={e => handleChangeFilterValue(e.target.name, Boolean(+e.target.value))}
                        />
                    </Box>
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default StatisticalLogFilters;